import type { MeetingDetailDTO } from "@/modules/management/meeting-minutes/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

interface MeetingPdfPreviewContentProps {
  meeting: MeetingDetailDTO;
}

const actionStatusLabels: Record<string, string> = {
  pending: "ລໍຖ້າ",
  done: "ສຳເລັດ",
};

function formatMeetingDate(s: string): string {
  try {
    return formatDateLocal(s);
  } catch {
    return s;
  }
}

export function MeetingPdfPreviewContent({
  meeting,
}: MeetingPdfPreviewContentProps) {
  return (
    <div
      id="meeting-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ບັນທຶກການປະຊຸມ</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ຫົວຂໍ້:{" "}
              <span className="font-normal">{meeting?.title || "ບໍ່ໄດ້ກໍານົດ"}</span>
            </p>
            <p className="mt-1 font-semibold">
              ວັນທີປະຊຸມ:{" "}
              <span className="font-normal">
                {meeting?.meetingDate
                  ? formatMeetingDate(meeting.meetingDate)
                  : "ບໍ່ໄດ້ກໍານົດ"}
              </span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Meeting Title */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          ບັນທຶກການປະຊຸມ
        </h2>
        <p className="font-semibold text-gray-700 text-xl">
          {meeting?.title || "ບໍ່ໄດ້ກໍານົດ"}
        </p>
      </div>

      {/* Section 1: ข้อมูลทั่วไป */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນທົ່ວໄປ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີປະຊຸມ
              </td>
              <td className="p-4 text-sm">
                {meeting?.meetingDate
                  ? formatMeetingDate(meeting.meetingDate)
                  : "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຫົວຂໍ້
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {meeting?.title || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            {meeting?.summary && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ສະຫຼຸບ
                </td>
                <td
                  className="p-4 text-sm leading-relaxed [&_ol]:list-decimal [&_ul]:list-disc"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: Summary is trusted HTML from TipTap editor
                  dangerouslySetInnerHTML={{ __html: meeting.summary }}
                />
              </tr>
            )}
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຜູ້ສ້າງບັນທຶກ
              </td>
              <td className="p-4 text-sm">
                {meeting?.createdByName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Section 2: ຜູ້ຮ່ວມ */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          2. ຜູ້ຮ່ວມ ({meeting?.attendees?.length ?? 0})
        </h3>

        {!meeting?.attendees?.length ? (
          <p className="p-4 text-muted-foreground text-sm">ບໍ່ມີຜູ້ຮ່ວມ</p>
        ) : (
          <table className="mb-6 w-full border-collapse border-2 border-gray-400">
            <thead>
              <tr className="border-gray-300 border-b bg-gray-100">
                <th className="border-gray-300 border-r p-4 text-left font-semibold text-gray-700">
                  ລຳດັບ
                </th>
                <th className="border-gray-300 border-r p-4 text-left font-semibold text-gray-700">
                  ຊື່ / ອີເມວ
                </th>
                <th className="p-4 text-left font-semibold text-gray-700">
                  ບົດບາດ
                </th>
              </tr>
            </thead>
            <tbody>
              {meeting.attendees.map((a, idx) => (
                <tr key={a.id} className="border-gray-300 border-b">
                  <td className="border-gray-300 border-r p-4 text-sm">
                    {idx + 1}
                  </td>
                  <td className="border-gray-300 border-r p-4 text-sm">
                    {a.userName ?? a.userEmail ?? a.userId}
                  </td>
                  <td className="p-4 text-sm">{a.role ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Section 3: ປະເດັນທີ່ລົມກັນ */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          3. ປະເດັນທີ່ລົມກັນ ({meeting?.actions?.length ?? 0})
        </h3>

        {!meeting?.actions?.length ? (
          <p className="p-4 text-muted-foreground text-sm">ບໍ່ມີປະເດັນ</p>
        ) : (
          <table className="mb-6 w-full border-collapse border-2 border-gray-400">
            <thead>
              <tr className="border-gray-300 border-b bg-gray-100">
                <th className="border-gray-300 border-r p-4 text-left font-semibold text-gray-700">
                  ລາຍລະອຽດ
                </th>
                <th className="border-gray-300 border-r p-4 text-left font-semibold text-gray-700">
                  ຜູ້ຮັບຜິດຊອບ
                </th>
                <th className="border-gray-300 border-r p-4 text-left font-semibold text-gray-700">
                  ວັນສົ່ງ
                </th>
                <th className="p-4 text-left font-semibold text-gray-700">
                  ສະຖານະ
                </th>
              </tr>
            </thead>
            <tbody>
              {meeting.actions.map((a) => (
                <tr key={a.id} className="border-gray-300 border-b">
                  <td
                    className="border-gray-300 border-r p-4 text-sm leading-relaxed [&_ol]:list-decimal [&_ul]:list-disc"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: Action description is trusted HTML from TipTap editor
                    dangerouslySetInnerHTML={{ __html: a.description }}
                  />
                  <td className="border-gray-300 border-r p-4 text-sm">
                    {a.assignees
                      .map((x) => x.userName ?? x.userId)
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </td>
                  <td className="border-gray-300 border-r p-4 text-sm">
                    {a.dueDate ? formatMeetingDate(a.dueDate) : "-"}
                  </td>
                  <td className="p-4 text-sm">
                    {actionStatusLabels[a.status] ?? a.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 border-gray-300 border-t-2 pt-6 text-center text-gray-600 text-sm">
        <p>ເອກະສານນີ້ຖືກສ້າງອັດຕະໂນມັດໂດຍລະບົບ Freedom ERP</p>
        <p className="mt-1">
          ວັນທີພິມ: {formatDateTimeLocal(new Date())} | ບັນທຶກ: {meeting?.id}
        </p>
      </div>
    </div>
  );
}
